import * as React from 'react'
import clx from 'classnames'
import { CompanyRole, PermissionFlag, PermissionSet } from '@/lib/api/company/roles'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import { FaIcon } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  groupLabel,
  has,
  impliedBy,
  PERMISSION_GROUPS,
  permissionDescription,
  permissionLabel,
  rolesKey,
  SCOPE_BADGE,
  scopeLabel,
} from './catalog'

export const ScopeBadge: React.FC<{ role: Pick<CompanyRole, 'scope'>; className?: string }> = ({ role, className }) => {
  const { t } = useLocalize()
  return (
    <span
      className={clx(
        'tw-inline-flex tw-whitespace-nowrap tw-rounded tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-medium',
        SCOPE_BADGE[role.scope],
        className,
      )}
    >
      {scopeLabel(t, role.scope)}
    </span>
  )
}

/** Stan „zaznacz wszystkie” grupy: wszystkie / część / żadne. */
export const groupState = (value: PermissionSet, flags: PermissionFlag[]) => {
  const count = flags.filter((f) => has(value, f)).length
  return { all: count === flags.length, some: count > 0 && count < flags.length }
}

/** Uprawnienia w kafelkach tematycznych (dwie kolumny); każde z jednym zdaniem, co odblokowuje. */
export const PermissionTable: React.FC<{
  value: PermissionSet
  editable: boolean
  onToggle?: (flag: PermissionFlag, value: boolean) => void
  onToggleGroup?: (flags: PermissionFlag[], value: boolean) => void
}> = ({ value, editable, onToggle, onToggleGroup }) => {
  const { t } = useLocalize()
  return (
    <div className="tw-grid tw-grid-cols-1 tw-items-start tw-gap-4 xl:tw-grid-cols-2">
      {PERMISSION_GROUPS.map((group) => {
        const state = groupState(value, group.flags)
        const groupId = `perm-group-${group.key}`
        return (
          <section key={group.key} className="tw-rounded-md tw-border tw-border-base-300 tw-bg-content-primary">
            <header className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-border-base-300 tw-px-3 tw-py-2.5">
              <Checkbox
                id={groupId}
                aria-label={`${t(rolesKey('selectAll'))}: ${groupLabel(t, group.key)}`}
                checked={state.all || state.some}
                indeterminate={state.some}
                disabled={!editable}
                onChange={() => onToggleGroup?.(group.flags, !state.all)}
              />
              <label
                htmlFor={groupId}
                className={clx(
                  'tw-mb-0 tw-flex tw-flex-1 tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-text-content-base',
                  editable && 'tw-cursor-pointer',
                )}
              >
                <FaIcon icon={group.icon} className="tw-h-4 tw-w-4 tw-text-primary" />
                {groupLabel(t, group.key)}
              </label>
              {editable && (
                <label htmlFor={groupId} className="tw-mb-0 tw-cursor-pointer tw-text-xs tw-font-medium tw-text-primary">
                  {state.all ? t(rolesKey('deselectAll')) : t(rolesKey('selectAll'))}
                </label>
              )}
            </header>
            <ul>
              {group.flags.map((flag) => {
                const source = impliedBy(value, flag)
                const checked = value[flag] || !!source
                const id = `perm-${flag}`
                const locked = !editable || !!source
                return (
                  <li
                    key={flag}
                    className={clx(
                      'tw-border-b tw-border-base-300/50 tw-transition-colors last:tw-border-b-0',
                      !locked && 'hover:tw-bg-primary/10',
                    )}
                  >
                    <label htmlFor={id} className={clx('tw-mb-0 tw-flex tw-gap-3 tw-px-3 tw-py-2', !locked && 'tw-cursor-pointer')}>
                      <Checkbox
                        id={id}
                        className="tw-mt-0.5 tw-flex-shrink-0"
                        checked={checked}
                        disabled={locked}
                        onChange={(e) => onToggle?.(flag, e.target.checked)}
                      />
                      <span className="tw-min-w-0">
                        <span
                          className={clx(
                            'tw-block tw-text-sm tw-text-content-base',
                            checked ? 'tw-font-medium' : 'tw-opacity-70',
                          )}
                        >
                          {permissionLabel(t, flag)}
                        </span>
                        <span className="tw-block tw-text-xs tw-text-content-base tw-opacity-60">
                          {permissionDescription(t, flag)}
                        </span>
                        {source && (
                          <span className="tw-block tw-text-[11px] tw-text-content-base tw-opacity-60">
                            {t(rolesKey('impliedBy'), { name: permissionLabel(t, source) })}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
    </div>
  )
}
